// THE TWO LAYOUTS.
//
// FOCUS — a narrow single column for the entry, the gates and checkout, the way a checkout flow runs.
// WORK  — the stepper rail and the work column. The prototype's third column, a "live case panel", is gone since
//         WEB-PARITY-5: ruling D4 replaced it with the app's own cards, on the steps that show them (see `Work`).
//
// The prototype's per-screen "Why this screen is built this way" disclosure is NOT ported. It was
// useful as a W4 spec artefact and the inventory says so (§4 "Better" #9), but it is not shipping UI,
// and several of its notes audited FALSE against the app (§5c).

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import type { AppealSnapshot } from "@contract";
import { AppealReportContext } from "../app/railAppeal";
import { useSession } from "../app/session";
import { reachedStep, stepHref, STEPS, stepOf, type WorkspaceRoute } from "../routes/steps";
import { afterText } from "./afterFilingCopy";
import { BrandLockup } from "./BrandLockup";
import { heldTemplate, heldText } from "./heldCopy";

/** The account screens themselves: "Sign in" from one of them goes back to wherever the owner was before, not to it. */
const ACCOUNT_PAGES = new Set(["/signin", "/forgot", "/account/action"]);

/**
 * NO STATUS CHIP (WEB-PARITY-1B, SW-4). The bar used to print the appeal's raw status enum — "spent", "shipped" — beside
 * the address. The app shows no status in its flow at all; its status words live on the Properties tab (`/home` here).
 * The status stays on the bar's element, `data-appeal-status`, for us.
 *
 * THE ACCOUNT CHIP (ACC-3a, F1). With the server's WEB_ACCOUNTS_ENABLED off it is today's "Account", to
 * `/delete-account`. On, it is "Sign in" to `/signin` while this browser is signed out (a look-around, or nobody), and
 * "Account" once it is signed in to an account — to `/account/settings` since ACC-3b (the app's Account screen, with Sign
 * out and "Delete account" on it). F1's words are the app's: ProfileTab.swift:184 and the app's Account screen title.
 *
 * "PROPERTIES" BESIDE IT, ONCE SIGNED IN (WALK-FIX-1, R236). `/home` lists the account's appeals, and
 * nothing linked to it: Tyler, 2026-10-05, found no way back to a saved property. The app's tab bar names it
 * "Properties" (MainTabView.swift:140, `home.tab`). Only for an account: a look-around's list is this browser's alone.
 * The bar stays ONE LINE at 390px (F-BRAND-1): the link never shrinks, the address chip gives way.
 *
 * AN ICON BESIDE EACH WORD (WALK-FIX-3, R240). Tyler, 2026-10-06, of "Properties" and "Account": "could
 * they be Icons or something instead or words?" They were hard to see: the condition step's tag rule (`.chips .chip`
 * now) shared the class `.chip` and was dimming the header's links to --cloud-dim with no border. Now each carries one
 * of the app's own SF Symbols, drawn inline — `house` for Properties (the app draws it for its Home tab, MainTabView.swift
 * :120; its Properties tab draws `building.2`, :121, and the house was the walk list's plan) and `person` for Account
 * (the person button in the app's header, HomeHero.swift:185) — beside its word on a wide screen and ALONE on a
 * phone (≤480px), where the word stays the link's text, hidden from sight only (`.nav-label`), so a screen reader still
 * reads "Properties" and "Account". "Sign in" keeps its word at every width: it is an action, and the bar has room.
 * No new words.
 */
export function AppBar({ addressChip, appealStatus, accountChip = "today" }: {
  addressChip?: string;
  appealStatus?: string;
  accountChip?: "today" | "signIn" | "account";
}) {
  const location = useLocation();
  const from = ACCOUNT_PAGES.has(location.pathname) ? undefined : location.pathname;
  const bar = useRef<HTMLElement>(null);
  // THE HEADER'S HEIGHT, FOR WHAT SITS UNDER IT (WALK-FIX-4): the Building overlay starts at `--appbar-h`, so the logo,
  // Properties and Account stay on screen while the letter is written. Measured, never assumed: the bar's height changes
  // with the width (WALK-FIX-3's phone icons) and the links shown.
  useLayoutEffect(() => {
    const el = bar.current;
    if (el === null) return;
    const root = document.documentElement;
    const measure = (): void => root.style.setProperty("--appbar-h", `${el.offsetHeight}px`);
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      root.style.removeProperty("--appbar-h");
    };
  }, []);
  return (
    <header ref={bar} className="appbar" data-appeal-status={appealStatus}>
      {/* The app's logo, dots and all, as proppeal.com draws it (WEB-BRAND-1; `BrandLockup.tsx` says why). */}
      <BrandLockup className="appbar-lockup" />
      {/* ONE LINE AT EVERY WIDTH (F-BRAND-1): a long address ends in an ellipsis instead of wrapping the bar taller (at
          390px it wrapped to three lines, then five beside the logo). The ellipsis is paint only: the full address stays
          the chip's text, which is what a screen reader reads, and its `title`, which a pointer shows on hover. */}
      {addressChip !== undefined && (
        <span className="chip address-chip" title={addressChip} data-address-chip>
          {addressChip}
        </span>
      )}
      <span className="spacer" />
      {accountChip === "account" && (
        <Link className="chip account-chip phone-icon" to="/home" data-properties-link data-app-copy="home.tab">
          <HouseIcon />
          <span className="nav-label">{afterText("home.tab")}</span>
        </Link>
      )}
      {/* Deletion is reachable from every screen, signed in or not, as the app's Account screen is (App Store
          Guideline 5.1.1(v); AccountManagementView.swift). WEB-PAY-1. */}
      {accountChip === "today" && (
        <Link className="chip account-chip phone-icon" to="/delete-account" data-account-link>
          <PersonIcon />
          <span className="nav-label">Account</span>
        </Link>
      )}
      {accountChip === "signIn" && (
        <Link className="chip account-chip" to="/signin" state={{ from }} data-account-link data-held-copy="account.chip.signIn">
          <PersonIcon />
          <span className="nav-label">{heldText("account.chip.signIn")}</span>
        </Link>
      )}
      {/* ACC-3b: "Account" goes to the account's settings, where Sign out and "Delete account" are (F-ACC3a-3). */}
      {accountChip === "account" && (
        <Link className="chip account-chip phone-icon" to="/account/settings" data-account-link data-held-copy="account.chip.account">
          <PersonIcon />
          <span className="nav-label">{heldText("account.chip.account")}</span>
        </Link>
      )}
    </header>
  );
}

/** SF Symbols' `house`, outline (the app's, MainTabView.swift:139): a roof with eaves, the walls and a door. Drawing only. */
function HouseIcon() {
  return (
    <svg className="nav-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="house">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.75 11.25 12 3.5l9.25 7.75M5.25 9.4v10.35c0 .55.45 1 1 1h11.5c.55 0 1-.45 1-1V9.4M10 20.75v-5.5c0-.55.45-1 1-1h2c.55 0 1 .45 1 1v5.5"
      />
    </svg>
  );
}

/** SF Symbols' `person`, outline (HomeHero.swift:185, the app header's account button): a head and shoulders. */
function PersonIcon() {
  return (
    <svg className="nav-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="person">
      <circle cx="12" cy="7.75" r="3.75" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M4.75 20.25c0-3.9 3.25-6.5 7.25-6.5s7.25 2.6 7.25 6.5c0 .3-.2.5-.5.5H5.25c-.3 0-.5-.2-.5-.5Z"
      />
    </svg>
  );
}

export function Focus({ wide, children }: { wide?: boolean; children: ReactNode }) {
  return <main className={wide === true ? "focus wide" : "focus"}>{children}</main>;
}

/**
 * The stepper rail.
 *
 * WHICH STEPS ARE REACHABLE IS THE SERVER'S ANSWER, not a high-water mark the client keeps. The
 * prototype tracked `state.reached` and let anything below it be clicked (app.js:705-706), which is one
 * half of why typing a URL walked past every gate (inventory §3 row 30). Here a step links only when
 * the appeal is unlocked; the links that are not live are rendered as plain text, so nothing offers a
 * route the guard would close (R112, the route half).
 *
 * EVERY STEP IS ONE SHAPE, LINK OR NOT (WEB-PARITY-1B, SW-1). The stylesheet styled only `.rail a`, so a step rendered
 * as a `<span>` — every step after this one, and every step while the appeal is locked — had no grid and no gap, and
 * read "2Condition". Both now carry `rail-step`, which is what `app.css` styles.
 *
 * THE APP'S SEVEN, AND ITS BAR (WEB-PARITY-5, SW-2/SW-3; `AppealProgressBar`, AppealProgressBar.swift:39-95): the
 * current step's name on the left and "Step n of 7" on the right, over seven capsules — the steps behind you filled
 * amber, the current one half filled, the rest empty. On a wide screen the list of steps sits under it, each a link
 * back once the appeal is unlocked; on a phone the list is a row of numbered 44-pixel targets. `/building`, the
 * overlay the app draws over step 6, sits on "Letter" (`stepOf`).
 */
export function Rail({ current, unlocked, appeal = null, covered = false }: {
  current: WorkspaceRoute;
  unlocked: boolean;
  appeal?: AppealSnapshot | null;
  /** Under the Building overlay (WALK-FIX-4): `inert`, out of the Tab order and a screen reader's reach. */
  covered?: boolean;
}) {
  const on = stepOf(current);
  const currentIndex = STEPS.findIndex((s) => s.path === on);
  // WALK-FIX-1: every step the server's snapshot says this appeal has reached stays a link, ahead of the one on screen
  // too, so "Revise from your details" never strands a finished letter behind step 1 (`reachedStep`, ../routes/steps.ts).
  // Read off the snapshot on every render and kept nowhere: not the prototype's high-water mark (row 30).
  const linkedThrough = Math.max(currentIndex, reachedStep(appeal));
  const label = STEPS[currentIndex]?.label ?? "";
  const count = heldTemplate("steps.count", { n: currentIndex + 1, total: STEPS.length });
  return (
    <nav className="rail" aria-label="Appeal steps" inert={covered}>
      <div className="rail-head" data-step-count={`${currentIndex + 1}/${STEPS.length}`}>
        <p className="rail-head-line">
          <b className="rail-now">{label}</b>
          <span data-held-copy="steps.count">{count}</span>
        </p>
        {/* The app's capsules (AppealProgressBar.swift:68-80). Drawing only: the list below names every step. */}
        <span className="rail-bar" aria-hidden="true">
          {STEPS.map((step, index) => (
            <span
              key={step.path}
              className={index < currentIndex ? "rail-seg done" : index === currentIndex ? "rail-seg now" : "rail-seg"}
            />
          ))}
        </span>
      </div>
      {STEPS.map((step, index) => {
        const state = index === currentIndex ? "now" : index < currentIndex ? "done" : index <= linkedThrough ? "reached" : "";
        const className = state === "" ? "rail-step" : `rail-step ${state}`;
        const mark = index < currentIndex ? "✓" : String(index + 1);
        const body = (
          <>
            <span className="n" aria-hidden="true">
              {mark}
            </span>
            <span className="label">{step.label}</span>
          </>
        );
        if (!unlocked || index > linkedThrough) {
          return (
            <span key={step.path} className={className} aria-current={index === currentIndex ? "step" : undefined}>
              {body}
            </span>
          );
        }
        return (
          <Link
            key={step.path}
            to={stepHref(step.path, appeal)}
            className={className}
            aria-current={index === currentIndex ? "step" : undefined}
            // The step's name, which a phone's numbered strip hides from sight but never from a screen reader.
            aria-label={step.label}
          >
            {body}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * THE WORKSPACE: the rail and the step, and nothing beside them (WEB-PARITY-5, ruling D4). The prototype's third column,
 * the "live case panel", had no counterpart in the app; D4 ruled that the app's own cards replace it, and Batches 3 to
 * 5 put them on the steps that show them in the app — the Value step's case strength and "You're requesting", the
 * Letter step's "Your case", Review's paper. So the panel, and the three calls it made on every step, are gone.
 */
export function Work({
  current,
  unlocked,
  appeal = null,
  children,
}: {
  current: WorkspaceRoute;
  unlocked: boolean;
  /** The shell's snapshot (one `v2Credits` `get` per page load), until a step hands the rail a newer one. */
  appeal?: AppealSnapshot | null;
  children: ReactNode;
}) {
  // THE NEWEST SNAPSHOT A STEP'S OWN CALL ANSWERED WITH (WALK-FIX-1, ../app/railAppeal.ts), for the appeal this tab holds.
  const { appealId } = useSession();
  const [reported, setReported] = useState<AppealSnapshot | null>(null);
  const fresh = reported !== null && reported.appealId === appealId ? reported : null;
  const snapshot = fresh ?? appeal;
  // UNDER THE BUILDING OVERLAY THE RAIL IS `inert` (WALK-FIX-4): covered, so out of the Tab order and out of a screen
  // reader's reach — the header above the overlay is the way out, and nothing hidden takes the focus on the way there.
  const covered = current === "/building";
  return (
    <AppealReportContext.Provider value={setReported}>
      <div className="work">
        <Rail current={current} unlocked={fresh !== null ? fresh.entitlement.unlocked : unlocked} appeal={snapshot} covered={covered} />
        <main id="work">{children}</main>
      </div>
    </AppealReportContext.Provider>
  );
}

export function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}
